import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, Code, School, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Served from /public as a stable, un-hashed URL so it can be preloaded from
// index.html — making the LCP image discoverable before the JS bundle parses.
const heroImage = "/hero-kids-coding.webp";

const features = [
  {
    icon: <Code className="w-4 h-4" aria-hidden="true" />,
    text: "Project-Based",
    id: "feature-project"
  },
  {
    icon: <School className="w-4 h-4" aria-hidden="true" />,
    text: "Expert-Led",
    id: "feature-expert"
  },
  {
    icon: <Users className="w-4 h-4" aria-hidden="true" />,
    text: "Collaborative",
    id: "feature-collab"
  }
];

export const HeroSection = ({ showTeacher = false, showBanner = false, studentLink, educatorLink, studentNumber }: { showTeacher?: boolean; showBanner?: boolean; studentLink?: string; educatorLink?: string; studentNumber?: number }) => {
  const navigate = useNavigate();
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section className={`relative min-h-screen flex items-center justify-center overflow-hidden ${showBanner ? "pt-52 sm:pt-40" : "pt-20 lg:pt-24"}`}>

        {/* Subtle spotlight overlay only */}
        <div className="pointer-events-none absolute inset-0 opacity-20" style={{ backgroundImage: 'var(--gradient-spotlight)' }} />

        <div className="container mx-auto py-12 lg:py-16 xl:py-24 relative z-10">
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center"
          >
            {/* Content */}
            <div className="space-y-5 lg:space-y-7 xl:space-y-8 text-center lg:text-left">
              <motion.div variants={item} className="flex justify-center lg:justify-start">
                <img src="wide-logo.png" alt="Byte-Sized Python" width={477} height={150} fetchPriority="high" className="max-w-[260px] lg:max-w-[320px] xl:max-w-none h-auto" />
                {/* <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl leading-tight font-bold text-foreground">Byte-Sized Python</h1> */}
              </motion.div>

              <motion.h1
                variants={item}
                className="font-heading text-4xl sm:text-5xl md:text-6xl leading-tight font-bold text-foreground"
              >
                Learn Python one <span className="text-primary">byte</span> at a time
              </motion.h1>

              <motion.p
                variants={item}
                className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto lg:mx-0"
              >
                Friendly, project-based lessons that make coding fun for kids and confidence-boosting for parents and educators.
              </motion.p>

              {/* Features */}
              <motion.div
                variants={item}
                role="list"
                aria-label="What makes our lessons special"
              >
                {features.map((feature, i) => (
                  <div
                    key={i}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 text-sm text-foreground border border-border backdrop-blur-sm transition-colors duration-200 mx-1"
                    role="listitem"
                  >
                    <span aria-hidden="true">{feature.icon}</span>
                    <span id={feature.id}>{feature.text}</span>
                  </div>
                ))}
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                variants={item}
                className="flex flex-col sm:flex-row gap-4 mt-8 justify-center lg:justify-start"
              >
                <Button
                  variant="hero"
                  size="xl"
                  className="group px-10 py-6 text-lg font-bold"
                  onClick={() => {
                    window.open(studentLink || '#', '_blank');
                  }}
                >
                  Join as a Student
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                {showTeacher && (
                  <Button
                    variant="outline"
                    size="xl"
                    className="px-8 py-6 text-lg font-medium border-primary text-primary hover:bg-primary/10 hover:border-primary transition-colors"
                    onClick={() => {
                      window.open(educatorLink || '#', '_blank');
                    }}
                  >
                    Join as an Educator
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="xl"
                  className="px-8 py-6 text-lg font-medium border-primary text-primary hover:bg-primary/10 hover:border-primary transition-colors"
                  onClick={() => navigate("/start-a-chapter")}
                >
                  Start a Chapter
                </Button>
              </motion.div>

              {/* Social Proof */}
              <motion.div
                variants={item}
                className="flex items-center gap-3 text-sm text-muted-foreground mt-8 justify-center lg:justify-start"
              >
                {/* <div className="flex -space-x-2">
                {[1, 2, 3].map((i) => (
                  <div 
                    key={i}
                    className="w-8 h-8 rounded-full border-2 border-background bg-foreground/5 flex items-center justify-center text-xs"
                  >
                    {i}
                  </div>
                ))}
              </div> */}
                <span>Trusted by {studentNumber}+ students</span>
              </motion.div>
            </div>

            {/* Hero Image — rendered immediately (no opacity animation) so it can
                paint as the LCP element without waiting on the entrance transition. */}
            <div
              className="relative h-[360px] lg:h-[440px] xl:h-[560px] w-full rounded-2xl overflow-hidden shadow-hero"
            >
              <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-white/0 z-10"></div>
              <img
                src={heroImage}
                alt="Students learning Python programming"
                width={1200}
                height={675}
                fetchPriority="high"
                loading="eager"
                decoding="async"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>

              {/* Achievement badges */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3">
                {["1 Year", `${studentNumber}+ Students`, "100% Free"].map((text, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + (i * 0.1) }}
                    className="bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium border border-white/60 shadow-sm text-center"
                  >
                    {text}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
  );
};